import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { api } from "../api";
import { effectiveDeliveryRate } from "../utils/deliveryFee";
const CartContext = createContext();

export const useCart = () => useContext(CartContext);
const API_KEY = process.env.REACT_APP_AUTH_TOKEN;
const apiUrl = process.env.REACT_APP_API_URL;
export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const stored = sessionStorage.getItem("cart");
    return stored ? JSON.parse(stored) : [];
  });
  const [orderId, setOrderId] = useState(null);
  const generateOrderId = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000000);
    return `${timestamp}-${random}`;
  };
  const updateCustomMeal = (id, updatedItem) => {
    setCart((prev) =>
      prev.map((item) => (item.id === id ? { ...updatedItem } : item))
    );
  };
  // add this small helper above sendCartToBackend (or wherever you keep utils)
  const buildCustomComponents = (components = {}) => ({
    protein: components.protein
      ? {
          id: components.protein.id,
          name: components.protein.name, // if your name is {ar, he}, it's fine – backend/email can stringify
          type: "protein",
        }
      : null,
    carb: components.carb
      ? {
          id: components.carb.id,
          name: components.carb.name,
          type: "carb",
        }
      : null,
    veggies: Array.isArray(components.veggies)
      ? components.veggies.map((v) => ({
          id: v.id,
          name: v.name,
          type: "veggie",
        }))
      : [],
    sauces: Array.isArray(components.sauces)
      ? components.sauces.map((v) => ({
          id: v.id,
          name: v.name,
          type: "sauce",
        }))
      : [],
    addons: Array.isArray(components.addons)
      ? components.addons.map((a) => ({
          id: a.id,
          name: a.name,
          type: a.type || "addon",
          price: a.addon_price ?? a.price ?? 0,
        }))
      : [],
  });

  // replace your whole sendCartToBackend with this version
  const sendCartToBackend = async (formData, discountData) => {
    const currentOrderId = orderId || generateOrderId();

    try {
      const orderProducts = cart.map((item) => {
        // keep bundles/meals structure you already had
        let formattedMeals = {};
        if (item.mealsByWeek) {
          item.mealsByWeek.forEach((weekMeals, index) => {
            formattedMeals[`week${index + 1}`] = weekMeals;
          });
        } else if (item.meals) {
          formattedMeals = { week1: item.meals };
        }

        // add custom meal block when relevant
        const customBlock = item.isCustom
          ? {
              isCustom: true,
              imageUrl: item.imageUrl || null,
              customComponents: buildCustomComponents(item.components),
            }
          : { isCustom: false };

        const row = {
          id: item.id,
          name: item.name,
          quantity: item.qty,
          price: item.price,
          bundle: formattedMeals, // stays {} for non-bundles
          totalMeals: item.totalMeals || null,
          weeks: item.weeks || 1,

          // NEW: make custom meals visible to backend/email
          ...customBlock,
        };
        if (item.skipMealSelection && item.description) {
          row.serviceNote = item.description;
        }
        return row;
      });

      const orderData = {
        orderId: currentOrderId,
        products: orderProducts,

        totalPrice:
          cart.reduce((acc, curr) => acc + curr.price * curr.qty, 0) +
          effectiveDeliveryRate(cart, formData?.deliveryRate),

        discount: discountData?.discount ?? null,
        discountTotal: discountData?.total ?? null,
        CouponCode: discountData?.code ?? "",

        customerName: `${formData.firstName} ${formData.lastName}`.trim(),
        customerEmail: formData.email,
        customerAddress: formData.address,
        customerPhone: formData.phone,
        paymentMethods: formData.payment,
        deliveryDate: formData.deliveryDate,
        status: "Pending",
        note: formData.note || "",
      };

      const response = await api.postCart(orderData);
      setOrderId(currentOrderId);

      console.log("Order API response:", response);
      return { success: true, data: response.data };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        switch (error.response.status) {
          case 400:
            return {
              success: false,
              error: "Invalid input",
              message: "Please check your input and try again.",
              code: 400,
            };
          case 401:
            return {
              success: false,
              error: "Unauthorized",
              message:
                "You are not authorized to place this order. Please log in and try again.",
              code: 401,
            };
          case 404:
            return {
              success: false,
              error: "Not Found",
              message:
                "The order submission endpoint was not found. Please try again later.",
              code: 404,
            };
          case 409:
            return {
              success: false,
              error: "Duplicate Order",
              message:
                "This order has already been submitted. Thank you for your order!",
              code: 409,
            };
          case 500:
            return {
              success: false,
              error: "Server Error",
              message:
                "An unexpected error occurred on the server. Please try again later.",
              code: 500,
            };
          default:
            return {
              success: false,
              error: "Unknown Error",
              message: "An unexpected error occurred. Please try again later.",
              code: error.response.status,
            };
        }
      } else if (error.request) {
        return {
          success: false,
          error: "No Response",
          message:
            "No response received from the server. Please check your internet connection and try again.",
          code: "NETWORK_ERROR",
        };
      } else {
        return {
          success: false,
          error: "Request Setup Error",
          message:
            "An error occurred while sending your order. Please try again.",
          code: "SETUP_ERROR",
        };
      }
    }
  };

  useEffect(() => {
    sessionStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const normalizeComponents = (components) => ({
    protein: components?.protein?.id || null,
    carb: components?.carb?.id || null,
    veggies: Array.isArray(components?.veggies)
      ? components.veggies.map((v) => v.id).sort()
      : [],
    sauces: Array.isArray(components?.sauces)
      ? components.sauces.map((v) => v.id).sort()
      : [],
    addons: Array.isArray(components?.addons)
      ? components.addons.map((a) => a.id).sort()
      : [],
  });

  const isSameCustomMeal = (a, b) => {
    return (
      JSON.stringify(normalizeComponents(a)) ===
      JSON.stringify(normalizeComponents(b))
    );
  };

  const addToCart = (product) => {
    setCart((prev) => {
      if (product.isCustom && product.components) {
        const existingCustom = prev.find(
          (item) =>
            item.isCustom &&
            isSameCustomMeal(item.components, product.components)
        );

        if (existingCustom) {
          return prev.map((item) =>
            isSameCustomMeal(item.components, product.components)
              ? { ...item, qty: item.qty + (product.qty || 1) }
              : item
          );
        }
      }

      const existing = prev.find((item) => item.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.id === product.id
            ? { ...item, qty: item.qty + (product.qty || 1) }
            : item
        );
      }

      return [...prev, { ...product, qty: product.qty || 1 }];
    });
  };

  const removeFromCart = (id) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  };

  const updateQty = (id, qty) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, qty: Math.max(1, qty) } : item
      )
    );
  };
  const clearCart = () => {
    setCart([]);
  };
  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateCustomMeal,
        updateQty,
        sendCartToBackend,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
